import { useEffect, useRef, useState } from 'react';
import StatusPill from './StatusPill';
import { INVOICE_STATUS_VARIANT, INVOICE_STATUS_LABEL } from '../lib/invoiceStatus.js';

// Phase 75 INV-04 / D-06: inline status dropdown — clicking the trigger pill opens
// a flat option list. MANAGER/ASSISTANT see only {draft, in_payment, void} per the
// RBAC matrix (audit-2 2026-07-22: awaiting_payment narrowed out); payment_canceled is
// offered only to ADMIN/ACCOUNTANT. A payment_canceled invoice is a hard terminal
// (audit-2 2026-07-22), and so is a paid invoice ([MF-24]: it leaves paid only through SQL) —
// the trigger is disabled, no transitions are offered. Frontend
// hint only — backend assertTransition is the authoritative gate, T-75-10-01.
//
// Phase 75.1 fix: previous version closed on `onMouseLeave` of the wrapping div, but the
// menu is `position: absolute` so the cursor leaves the wrapper's hit-box the moment it
// enters the menu — making the dropdown unusable. Replaced with document-level click-outside
// + Escape.
export default function InvoiceStatusDropdown ({ currentStatus, role, onChange, disabled, appearance }) {
  const ALLOWED = (role === 'MANAGER' || role === 'ASSISTANT')
    ? ['draft', 'in_payment', 'void']
    : (role === 'ADMIN' || role === 'ACCOUNTANT')
      ? ['draft', 'in_payment', 'awaiting_payment', 'in_progress', 'paid', 'payment_canceled', 'void']
      : ['draft', 'in_payment', 'awaiting_payment', 'in_progress', 'paid', 'void'];
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const safeStatus = currentStatus || 'draft';

  const isPaid = safeStatus === 'paid';
  const isDisabled = disabled || safeStatus === 'payment_canceled' || isPaid;

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (ev) => {
      if (ref.current && !ref.current.contains(ev.target)) setOpen(false);
    };
    const onKey = (ev) => { if (ev.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="invoice-status-dropdown"
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        type="button"
        onClick={() => !isDisabled && setOpen((o) => !o)}
        disabled={isDisabled}
        title={isPaid ? 'Оплаченный инвойс не редактируется' : isDisabled ? undefined : 'Сменить статус инвойса'}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: isDisabled ? 0.7 : 1,
        }}
      >
        {/* P-D2 D14: the TRIGGER pill forwards `appearance`; the option-menu pills below
            stay solid — a tinted chip on a white popover reads as a filter, not a menu item. */}
        <StatusPill
          variant={INVOICE_STATUS_VARIANT[safeStatus] || 'muted'}
          label={INVOICE_STATUS_LABEL[safeStatus] || safeStatus}
          appearance={appearance}
        />
      </button>
      {open && !isDisabled && (
        <div
          className="invoice-status-dropdown__menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            background: '#fff',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            padding: 6,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            boxShadow: '0 4px 12px rgba(15,23,42,0.10)',
            whiteSpace: 'nowrap',
          }}
        >
          {ALLOWED.map((s) => {
            const isCurrent = s === safeStatus;
            return (
              <button
                key={s}
                type="button"
                disabled={isCurrent}
                onClick={() => { setOpen(false); onChange(s); }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '4px 6px',
                  textAlign: 'left',
                  cursor: isCurrent ? 'default' : 'pointer',
                  opacity: isCurrent ? 0.55 : 1,
                }}
              >
                <StatusPill
                  variant={INVOICE_STATUS_VARIANT[s]}
                  label={INVOICE_STATUS_LABEL[s]}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
